import { gql, useQuery } from '@apollo/client';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';
import { useState } from 'react';
import { useSimpleError as useError } from '@ts-private/apollo-tools-ts';

const useAlertError = () => {
  const showError = useError(message => alert(message));
  return showError;
};

export const HAS_AUTHORIZATION_QUERY = gql`
  query hasAuthorization($codes: [String!]!, $documentStateId: Int, $documentTypeId: Int) {
    hasAuthorization(codes: $codes, documentStateId: $documentStateId, documentTypeId: $documentTypeId) {
      code
      hasAuthorization
      documentStateId
      documentTypeId
    }
  }
`;

/**
 * Get authorization by code. If authorization is refined, also pass documentTypeId and documentStateId
 * @param {string} code
 * @param {number | undefined} documentTypeId
 * @param {number | undefined} documentStateId
 * @returns {boolean}
 */
export function useAuthorization(code, documentTypeId = null, documentStateId = null) {
  const authorization = useAuthorizations([code], documentTypeId, documentStateId);
  return Object.values(authorization)[0];
}

export function useAuthorizations(codes, documentTypeId = null, documentStateId = null) {
  const [result, setResult] = useState({});
  const showError = useAlertError();

  const searchedCodes = Object.values(AuthorizationCodes)
    .filter(key => codes.includes(key))
    .reduce((accumulator, currentValue) => {
      return {
        ...accumulator,
        [currentValue]: Object.keys(AuthorizationCodes).find(key => AuthorizationCodes[key] === currentValue)
      };
    }, {});

  // not using useQueryWithErrorHandling because it will trigger an infinite loop, since it's using useToast, which uses this hook also
  useQuery(HAS_AUTHORIZATION_QUERY, {
    variables: { codes: Object.keys(searchedCodes), documentStateId, documentTypeId },
    skip: !searchedCodes,
    onCompleted: data => {
      const authorizations = data?.hasAuthorization?.reduce((accumulator, currentValue) => {
        return {
          ...accumulator,
          [Object.keys(AuthorizationCodes).find(key => AuthorizationCodes[key] === currentValue.code)]: currentValue.hasAuthorization
        };
      }, {});
      setResult(authorizations);
    },
    onError: error => {
      showError(error);
    }
  });
  return result;
}
